import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadAdminKindergartens } from './financeDirectory';
import { KindergartenBasicInfoDto } from '../../common/interfaces/kindergarten.interface';
import { createNotification } from '../../utilities/helper/notification.helper';
import { MESSAGE_TYPE } from '../../common/constants/common.constants';
import { useTranslation } from 'react-i18next';

/**
 * The kindergarten every finance screen is scoped to, kept in the URL.
 *
 * Two differences from how the invoices workspace used to do this, both of which became
 * load-bearing once the section grew sub-pages:
 *
 * - `?kindergartenId=` is read on every search change, not once on mount, so moving
 *   between the dashboard and a drill-down does not lose it.
 * - An auto-selected kindergarten is written back into the URL. Previously a single-
 *   kindergarten admin had a URL with no id in it, which was merely untidy while there
 *   was one screen; a `/finance/location/:id` link without it can fetch nothing at all.
 */
export const useFinanceKindergarten = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [kindergartens, setKindergartens] = useState<KindergartenBasicInfoDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fromUrl = new URLSearchParams(location.search).get('kindergartenId') ?? '';

  const setKindergartenId = useCallback(
    (next: string) => {
      const params = new URLSearchParams(location.search);
      if (next) params.set('kindergartenId', next);
      else params.delete('kindergartenId');
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    },
    [navigate, location.pathname, location.search]
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const items = await loadAdminKindergartens();
        if (cancelled) return;
        setKindergartens(items);
        // one kindergarten is the common case, so do not make the admin pick it
        if (!fromUrl && items.length === 1) {
          const params = new URLSearchParams(location.search);
          params.set('kindergartenId', items[0].id);
          navigate(`${location.pathname}?${params.toString()}`, { replace: true });
        }
      } catch (error) {
        if (!cancelled) {
          createNotification(MESSAGE_TYPE.ERROR, t('kindergartens.loadFailed'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // the list does not depend on the URL; the auto-select reads it once, on arrival
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    kindergartens,
    kindergartenId: fromUrl,
    kindergartenName: kindergartens.find(item => item.id === fromUrl)?.name,
    setKindergartenId,
    isLoading
  };
};
